import { orderTransactionsService } from '@afterpaytouch/portal-api/consumer/ordertransactions'
import { CommentDisputeRequest, disputeService } from '@afterpaytouch/portal-api/consumer/disputes'
import { Chargeback, DisputeInfoResponse } from '@afterpaytouch/portal-api/consumer/ordertransactions/types'

import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'

import { createModalHook } from '../../hooks/useModal'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LaunchDarklyFlags, useFlag } from '../../hooks'
import { isDisputeClosed } from './utils'
import { ProcessDisputeAction, ProcessDisputeNote } from '../../model'
import { useDisputeInfoModalContext } from './DisputeInfoContext'
import { useToast } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import { TrackingEvent } from '../../model/amplitude'
import { setDisputePermissionError, setIsDisputePermitted, useAppDispatch } from '../../state'

export interface UseDisputeInfoModal {
  isDisputed: boolean
  isDisputeClosed: boolean
  disputeInfo: DisputeInfoResponse
  chargebackBannerVisible: boolean
  chargebacks: Chargeback[]
  consumerDisplayState: string
  isModalOpen: boolean
  openModal: () => void
  closeModal: () => void
  refresh: () => void
}

export interface UseProcessDispute {
  cancel: (id: number) => void
  accept: (id: number) => void
  reject: (data: CommentDisputeRequest) => void
  submitEvidence: (data: CommentDisputeRequest) => void
  isProcessError: boolean
  isCommentError: boolean
  isSubmitEvidenceError: boolean
}

const { useGetDisputeInfoMutation, useGetChargebackInfoMutation } = orderTransactionsService
const useModal = createModalHook()

export const useDisputeInfoModal = (orderId: string): UseDisputeInfoModal => {
  const dispatch = useAppDispatch()
  const [chargebacks, setChargebacks] = useState<Chargeback[]>([])
  const [getDisputeInfo, { data, isSuccess }] = useGetDisputeInfoMutation()
  const [getChargebackInfo, { data: chargebackData, isSuccess: isGetChargebackSuccess }] = useGetChargebackInfoMutation()
  const disputedInfoEnabled: boolean = useFlag(LaunchDarklyFlags.IrDisputeTrackingConsumerPortalEnabled)
  const disputePermissionControlEnabled: boolean = useFlag(LaunchDarklyFlags.IrDisputePermissionControlEnabled)
  const chargebackInfoEnabled: boolean = useFlag(LaunchDarklyFlags.IrChargebackInfoEnabled)
  const [isModalOpen, { openModal, closeModal }] = useModal()
  const isDisputed = useMemo(() => {
    // @ts-ignore: OPERATION BLEED STOPPER
    return data?.disputeEntities?.length > 0
  }, [data])
  const closed = useMemo(() => {
    // @ts-ignore: OPERATION BLEED STOPPER
    return isDisputeClosed(data)
  }, [data])
  const consumerDisplayState = useMemo(() => {
    return data?.disputeEntities[0]?.consumerDisplayState?.replace(/_/g, ' ').toLowerCase() ?? ''
  }, [data])

  const chargebackBannerVisible = useMemo(() => {
    return Array.isArray(chargebacks) && chargebacks.length > 0
  }, [chargebacks])

  const refresh = useCallback(() => {
    if (disputedInfoEnabled || disputePermissionControlEnabled) {
      getDisputeInfo({
        orderId,
        type: 'ORDER_DISPUTE_CASE',
      })
    }

    if (chargebackInfoEnabled) {
      getChargebackInfo({
        orderId,
      })
    }
  }, [disputedInfoEnabled, disputePermissionControlEnabled, chargebackInfoEnabled, getDisputeInfo, orderId, getChargebackInfo])

  useEffect(() => {
    if (disputedInfoEnabled || disputePermissionControlEnabled) {
      getDisputeInfo({
        orderId,
        type: 'ORDER_DISPUTE_CASE',
      })
    }
  }, [getDisputeInfo, orderId, disputedInfoEnabled, disputePermissionControlEnabled])

  useEffect(() => {
    if (chargebackInfoEnabled) {
      getChargebackInfo({
        orderId,
      })
    }
  }, [chargebackInfoEnabled, getChargebackInfo, orderId])

  useEffect(() => {
    if (isSuccess) {
      dispatch(setIsDisputePermitted(data?.isDisputePermitted ?? true))
      dispatch(setDisputePermissionError(data?.disputePermissonError))
    }
  }, [isSuccess, data, dispatch])

  useEffect(() => {
    if (isGetChargebackSuccess && typeof chargebackData !== 'undefined' && Array.isArray(chargebackData.results)) {
      setChargebacks(chargebackData.results)
    }
  }, [isGetChargebackSuccess, chargebackData])

  return {
    isDisputed,
    isDisputeClosed: closed,
    // @ts-ignore: OPERATION BLEED STOPPER
    disputeInfo: data,
    // @ts-ignore: OPERATION BLEED STOPPER
    consumerDisplayState,
    isModalOpen,
    openModal,
    closeModal,
    refresh,
    chargebacks,
    chargebackBannerVisible,
  }
}

const { useProcessDisputeMutation, useCommentDisputeMutation, useSubmitDisputeEvidenceMutation } = disputeService
const I18N_NAMESPACE = ['dispute']
export const useProcessDispute = (): UseProcessDispute => {
  const setToast = useToast()
  const { t } = useTranslation(I18N_NAMESPACE)
  const { setIsLoading, closeModal, refresh } = useDisputeInfoModalContext()
  const [processDispute, { isLoading: isProcessLoading, isSuccess: isProcessSuccess, isError: isProcessError }] = useProcessDisputeMutation()
  const [commentDispute, { isLoading: isCommentLoading, isSuccess: isCommentSuccess, isError: isCommentError }] = useCommentDisputeMutation()
  const [submitDisputeEvidence, { isLoading: isSubmitLoading, isSuccess: isSubmitSuccess, isError: isSubmitError }] = useSubmitDisputeEvidenceMutation()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  const cancel = useCallback(
    (id: number) => {
      processDispute({
        id,
        action: ProcessDisputeAction.CancelDispute,
        note: ProcessDisputeNote.CancelDispute,
      })
    },
    [processDispute]
  )
  const accept = useCallback(
    (id: number) => {
      processDispute({
        id,
        action: ProcessDisputeAction.AcceptDispute,
        note: ProcessDisputeNote.AcceptDispute,
      })
    },
    [processDispute]
  )
  const reject = useCallback(
    (data: CommentDisputeRequest) => {
      commentDispute(data)
    },
    [commentDispute]
  )
  const submitEvidence = useCallback(
    (data: CommentDisputeRequest) => {
      submitDisputeEvidence(data)
    },
    [submitDisputeEvidence]
  )

  useEffect(() => {
    setIsLoading(isCommentLoading || isProcessLoading || isSubmitLoading)
  }, [isCommentLoading, isProcessLoading, isSubmitLoading, setIsLoading])

  useEffect(() => {
    if (isProcessSuccess || isCommentSuccess || isSubmitSuccess) {
      setToast({
        message: t('dispute:info:processMessage:success'),
        toastTime: 10000,
        manualDismiss: true,
        kind: 'Success',
      })
      logEvent(TrackingEvent.VIEWED_DISPUTE_INFO_PROCESS_SUCCESS)
      closeModal()
      refresh()
    }
  }, [isProcessSuccess, isCommentSuccess, isSubmitSuccess, logEvent])

  useEffect(() => {
    if (isProcessError || isCommentError || isSubmitError) {
      logEvent(TrackingEvent.VIEWED_DISPUTE_INFO_PROCESS_FAILURE)
    }
  }, [isProcessError, isCommentError, isSubmitError, logEvent])

  return {
    cancel,
    accept,
    reject,
    submitEvidence,
    isProcessError,
    isCommentError,
    isSubmitEvidenceError: isSubmitError,
  }
}
